import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";

const CFG = {
  visita: { entity: "visite", title: "Visita", fields: [
    { k: "tipologia", l: "Tipologia", t: "select", o: ["familiare", "ASL", "medica", "fisioterapista", "assistente sociale", "tecnico", "controllo istituzionale", "altra"] },
    { k: "visitatore", l: "Visitatore" },
    { k: "data", l: "Data", t: "date" }, { k: "ora_inizio", l: "Ora", req: true },
    { k: "stato", l: "Stato", t: "select", o: ["programmato", "confermato", "completato", "annullato"] },
    { k: "note", l: "Note", t: "textarea" },
  ]},
  uscita: { entity: "uscite", title: "Uscita", fields: [
    { k: "tipo", l: "Tipo", t: "select", o: ["uscita con parenti", "pranzo o cena", "passeggiata", "evento", "vacanza", "commissione", "altro"] },
    { k: "motivo", l: "Motivo" }, { k: "data", l: "Data", t: "date" }, { k: "ora", l: "Ora", req: true },
    { k: "accompagnatore", l: "Accompagnatore" },
    { k: "stato", l: "Stato", t: "select", o: ["programmato", "in corso", "rientrato", "annullato"] },
    { k: "note", l: "Note", t: "textarea" },
  ]},
  ospedale: { entity: "ospedale", title: "Ricovero / Ospedale", fields: [
    { k: "tipologia", l: "Tipologia", t: "select", o: ["ricovero", "pronto soccorso", "visita programmata", "esame diagnostico"] },
    { k: "ospedale", l: "Ospedale / struttura" }, { k: "reparto", l: "Reparto" }, { k: "motivo", l: "Motivo" },
    { k: "data", l: "Data", t: "date" }, { k: "ora", l: "Ora", req: true },
    { k: "stato", l: "Stato", t: "select", o: ["ricoverato", "programmato", "rientrato"] },
    { k: "note", l: "Note", t: "textarea" },
  ]},
};
const TYPE_OPTS = [["visita", "Visita"], ["uscita", "Uscita"], ["ospedale", "Ricovero / Ospedale"]];

export default function QuickEventDialog({ open, type, ospite, existing, onOpenChange, onSaved, pickOspite, defaultDate, structureId }) {
  const { selectedStructure } = useAuth();
  const [selType, setSelType] = useState(type || "visita");
  const [selOspiteId, setSelOspiteId] = useState("");
  const [ospitiList, setOspitiList] = useState([]);
  const [form, setForm] = useState({});

  const effType = type || selType;
  const cfg = effType ? CFG[effType] : null;

  useEffect(() => {
    if (open && pickOspite) {
      const sid = structureId && structureId !== "all" ? structureId : selectedStructure;
      api.get("/ospiti", { params: { structure_id: sid } }).then((r) => setOspitiList(r.data));
    }
  }, [open, pickOspite, structureId, selectedStructure]);

  useEffect(() => {
    if (!open) return;
    if (existing) { setForm(existing); if (!type && existing._type) setSelType(existing._type); return; }
    setSelType(type || "visita");
    setSelOspiteId("");
    setForm({ data: defaultDate || new Date().toISOString().slice(0, 10) });
  }, [open, type, existing, defaultDate]);

  useEffect(() => {
    if (!open || existing) return;
    setForm((p) => ({ ...p, stato: CFG[effType]?.fields.find((f) => f.k === "stato")?.o[0] }));
  }, [effType, open]);

  if (!cfg) return null;
  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const effOspite = ospite || ospitiList.find((o) => o.id === selOspiteId);

  const save = async () => {
    const payload = { ...form };
    if (effOspite) {
      payload.ospite_id = effOspite.id;
      payload.ospite_nome = `${effOspite.nome || ""} ${effOspite.cognome || ""}`.trim();
      payload.structure_id = payload.structure_id || effOspite.structure_id;
    }
    if (pickOspite && !payload.ospite_id) { toast.error("Seleziona un ospite"); return; }
    if (!payload.structure_id) { toast.error("Struttura ospite mancante"); return; }
    const missing = cfg.fields.filter((f) => f.req && !payload[f.k]);
    if (missing.length) { toast.error(`Compila i campi obbligatori: ${missing.map((f) => f.l).join(", ")}`); return; }
    delete payload._type;
    try {
      if (existing?.id) await api.put(`/${cfg.entity}/${existing.id}`, payload);
      else await api.post(`/${cfg.entity}`, payload);
      toast.success("Salvato");
      onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const remove = async () => {
    try { await api.delete(`/${cfg.entity}/${existing.id}`); toast.success("Eliminato"); onSaved && onSaved(); }
    catch { toast.error("Errore"); }
  };

  const heading = existing ? `Modifica ${cfg.title}` : (pickOspite ? "Nuovo evento" : `Nuovo/a ${cfg.title}`);
  const sub = ospite ? `${ospite?.nome} ${ospite?.cognome}` : (existing?.ospite_nome || "");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{heading}{sub ? ` — ${sub}` : ""}</DialogTitle>
          <DialogDescription>Compila i dettagli dell'evento. L'orario è obbligatorio.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {!type && !existing && (
            <div className="col-span-2">
              <Label className="text-xs font-semibold mb-1 block">Tipo evento *</Label>
              <Select value={selType} onValueChange={setSelType}>
                <SelectTrigger data-testid="qe-type"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPE_OPTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {pickOspite && !existing && (
            <div className="col-span-2">
              <Label className="text-xs font-semibold mb-1 block">Ospite *</Label>
              <Select value={selOspiteId} onValueChange={setSelOspiteId}>
                <SelectTrigger data-testid="qe-ospite"><SelectValue placeholder="Seleziona ospite" /></SelectTrigger>
                <SelectContent>{ospitiList.map((o) => <SelectItem key={o.id} value={o.id}>{o.nome} {o.cognome}{o.camera ? ` — cam. ${o.camera}` : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {cfg.fields.map((f) => (
            <div key={f.k} className={f.t === "textarea" ? "col-span-2" : ""}>
              <Label className="text-xs font-semibold mb-1 block">{f.l}{f.req && " *"}</Label>
              {f.t === "textarea" ? (
                <Textarea value={form[f.k] || ""} onChange={(e) => setF(f.k, e.target.value)} data-testid={`qe-${f.k}`} />
              ) : f.t === "select" ? (
                <Select value={form[f.k] || ""} onValueChange={(v) => setF(f.k, v)}>
                  <SelectTrigger data-testid={`qe-${f.k}`}><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>{f.o.map((o) => <SelectItem key={o} value={o} className="capitalize">{o}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <Input type={f.t === "date" ? "date" : f.k.startsWith("ora") ? "time" : "text"} value={form[f.k] || ""} onChange={(e) => setF(f.k, e.target.value)} data-testid={`qe-${f.k}`} />
              )}
            </div>
          ))}
        </div>
        <DialogFooter className="gap-2">
          {existing?.id && <Button variant="outline" onClick={remove} className="mr-auto text-destructive" data-testid="qe-delete">Elimina</Button>}
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={save} data-testid="qe-save" className="font-semibold">Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
